/**
 * PlanViewer — container for the product floor-plan viewer.
 *
 * Responsibilities:
 * - Reads product + tier + projectDataUrl from ProductContext and AppContext
 * - Delegates fetch/cache to useProductSvg
 * - Shows loading / error states
 * - Routes to the correct mode renderer based on activeMode
 * - Non-axonometric modes are wrapped in TransformWrapper (Phase C) for zoom/pan.
 *   Axonometric has its own drag interaction and is rendered outside the wrapper.
 */

import { useRef } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import { useApp } from '../../../../app/AppContext.jsx'
import { useProduct } from '../../index.js'
import { OnboardingContextProvider } from '../../context/OnboardingContext.jsx'

import { FETCH_STATUS } from '../../../../shared/constants/fetchStatus.js'

import { useProductSvg } from '../../hooks/useProductSvg.js'
import { useCompartmentVariants } from '../../hooks/useCompartmentVariants.js'
import { useVariantedSvg } from '../../hooks/useVariantedSvg.js'
import { PRODUCT_VIEW_MODE } from '../../constants/viewModes.js'

import { ArrangeOnboarding } from './ArrangeOnboarding.jsx'
import { AxonometricOnboarding } from './AxonometricOnboarding.jsx'
import { HotSpotOnboarding } from './HotSpotOnboarding.jsx'
import { InfoOnboarding } from './InfoOnboarding.jsx'
import { InsolationOnboarding } from './InsolationOnboarding.jsx'
import { PlanRendererArrangeMode } from './PlanRendererArrangeMode.jsx'
import { PlanRendererAxonometricMode } from './PlanRendererAxonometricMode.jsx'
import { PlanRendererInfoMode } from './PlanRendererInfoMode.jsx'
import { PlanRendererInsolationMode } from './PlanRendererInsolationMode.jsx'
import { PlanRendererHotSpotMode } from './PlanRendererHotSpotMode.jsx'
import { OnboardingOverlay } from './OnboardingOverlay.jsx'
import { ViewerToolbar } from './ViewerToolbar.jsx'

import { BimCadaplusLogoTag } from '../../../../shared/ui/BimCadaplusLogoTag/BimCadaplusLogoTag.jsx'

import styles from './PlanViewer.module.scss'

// ── Onboarding content router ─────────────────────────────────────────────────

function OnboardingContent({ modeId }) {
  switch (modeId) {
    case PRODUCT_VIEW_MODE.INSOLATION:
      return <InsolationOnboarding />
    case PRODUCT_VIEW_MODE.INFO:
      return <InfoOnboarding />
    case PRODUCT_VIEW_MODE.HOTSPOT:
      return <HotSpotOnboarding />
    case PRODUCT_VIEW_MODE.ARRANGE:
      return <ArrangeOnboarding />
    case PRODUCT_VIEW_MODE.AXONOMETRIC:
      return <AxonometricOnboarding />
    default:
      return null
  }
}

// ── Placeholder renderers (filled in Phase 3) ─────────────────────────────────

function RendererStub({ modeId }) {
  return (
    <div className={styles.rendererStub}>
      <p>{modeId}</p>
    </div>
  )
}

// ── Loading / Error states ────────────────────────────────────────────────────

function PlanLoading() {
  return <div className={styles.stateContainer} aria-busy="true"><span className={styles.loadingSpinner} /></div>
}

function PlanError({ message }) {
  return (
    <div className={styles.stateContainer} role="alert">
      <p className={styles.errorMessage}>{message ?? 'Nie można załadować rzutu'}</p>
    </div>
  )
}

// ── PlanViewer ────────────────────────────────────────────────────────────────

export function PlanViewer() {
  const { windowData, isMobile } = useApp()
  const { selection, viewerState, modes, zoomBridgeRef } = useProduct()

  const { product } = selection
  const { selectedLevelId, selectedVariants, variantsCount } = viewerState
  const activeMode = modes.activeMode
  const isAxonometric = activeMode === PRODUCT_VIEW_MODE.AXONOMETRIC

  // Imperative ref for the inset-shadow overlay div — set from onTransformed
  // without triggering a React re-render (which would remount TransformWrapper).
  const zoomShadowRef = useRef(null)

  // Resolve active tier: user-selected level or product default
  const refTier = selectedLevelId ?? product?.tiers?.[0]?.ref_tier ?? null

  const { svgElement: baseSvg, svgDimensions, status, error } = useProductSvg({
    product,
    refTier,
    projectDataUrl: windowData?.projectDataUrl ?? null,
  })

  // ── Variant-aware SVG ─────────────────────────────────────────────────────
  // Lazily fetch every alternative-variant fragment (cache is module-level →
  // free after first load). The varianted clone is what non-Arrange renderers
  // consume so a selection made in Arrange persists across mode switches.
  // Arrange itself keeps the **base** svg to retain its in-place mutate
  // strategy (no remount while cycling).

  const { getVariantNode } = useCompartmentVariants({
    product,
    projectDataUrl: windowData?.projectDataUrl ?? null,
    variantsCount,
    enabled: variantsCount.some((c) => c > 1),
  })

  const variantedSvg = useVariantedSvg({
    baseSvg,
    selectedVariants,
    getVariantNode,
  })

  // ── Render states ──────────────────────────────────────────────────────────

  if (!product) return null

  if (status === FETCH_STATUS.LOADING) {
    return (
      <div className={styles.viewer}>
        <PlanLoading />
      </div>
    )
  }

  if (status === FETCH_STATUS.ERROR) {
    return (
      <div className={styles.viewer}>
        <PlanError message={error} />
      </div>
    )
  }

  // ── Mode routing ───────────────────────────────────────────────────────────

  function renderModeContent() {
    switch (activeMode) {
      case PRODUCT_VIEW_MODE.INFO:
        return <PlanRendererInfoMode svgElement={variantedSvg} svgDimensions={svgDimensions} />

      case PRODUCT_VIEW_MODE.INSOLATION:
        return <PlanRendererInsolationMode svgElement={variantedSvg} svgDimensions={svgDimensions} />

      case PRODUCT_VIEW_MODE.HOTSPOT:
        return <PlanRendererHotSpotMode svgElement={variantedSvg} svgDimensions={svgDimensions} />

      case PRODUCT_VIEW_MODE.AXONOMETRIC:
        return <PlanRendererAxonometricMode />

      case PRODUCT_VIEW_MODE.ARRANGE:
        return (
          <PlanRendererArrangeMode
            svgElement={baseSvg}
            svgDimensions={svgDimensions}
            getVariantNode={getVariantNode}
          />
        )

      default:
        return <PlanRendererInfoMode svgElement={variantedSvg} svgDimensions={svgDimensions} />
    }
  }

  return (
    <OnboardingContextProvider activeMode={activeMode}>
      <div className={styles.viewer}>
        <ViewerToolbar isMobile={isMobile} />
        <div className={styles.frame}>
          {isAxonometric
            ? renderModeContent()
            : (
              <>
                <TransformWrapper
                  ref={zoomBridgeRef}
                  wheel={{ disabled: true }}
                  doubleClick={{ disabled: true }}
                  panning={{ excluded: ['bim-sun-drag'] }}
                  onTransform={(_, state) => {
                    if (!zoomShadowRef.current) return
                    if (state.scale > 1) {
                      zoomShadowRef.current.dataset.zoomed = ''
                    } else {
                      delete zoomShadowRef.current.dataset.zoomed
                    }
                  }}
                >
                  <TransformComponent
                    wrapperStyle={{ width: '100%', height: '100%' }}
                    contentStyle={{ width: '100%', height: '100%' }}
                  >
                    {renderModeContent()}
                  </TransformComponent>
                </TransformWrapper>
                <div ref={zoomShadowRef} className={styles.zoomShadow} />
              </>
            )
          }
          <OnboardingOverlay activeMode={activeMode}>
            <OnboardingContent modeId={activeMode} />
          </OnboardingOverlay>
        </div>
        <BimCadaplusLogoTag 
          size={isMobile ? "small" : "medium"} 
          position="bottom" 
          className={styles.logoOverlay} 
        /> 
      </div>
    </OnboardingContextProvider>
  )
}
